/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { NextPagesNavigator } from '@meitu/catalyze-next';
import Head from 'next/head';
import Script from 'next/script';
import { extractUTMParams } from '@meitu/tracer-utm-params';
import { createCommonParamsMiddleware } from '$/api/middlewares/createCommonParamsMiddleware';
import { getIsOverseaByCountryCode } from '$/common/utils/countryCodeUtil';
import localStorageUtil from '$/common/utils/localStorageUtil';
import { LanguageCodeType } from '$/store/languageStore';
import { InitComponent, InitNavigateMap } from '$/store/useOfficialStore';
import {
  getChannel, getMatrixChannel, initChannel, getFirstURL,
} from '$/utils/channelUtil';

import {
  getEnv, navigate, pageAgent, statAgent,
} from '@/common/agents';
import { defineNextSubApp, renderNextPage } from '@/common/next';
import { getGid } from '@/utils';
import { createErrorMiddleware } from '@/apiSdk/errorMiddleware';
import { createCanvasErrorMiddleware } from '@/apiSdk/errorCanvasMiddleware';
import { initMTLogin, getUID, getAccessToken } from '@/common/utils/accountUtil';
import { loadMtstatSdkScript } from '@/common/loadMtstatSdkScript';
import { LanguageCode } from '$/store/languageStore/types';
import { isInLangList, mapLanguageCode } from '$/store/languageStore/utils';
// import Header from '@/overseaComponents/layout/header';
// import Footer from '@/overseaComponents/layout/footer';
import styles from '@/components/overseaHome/layout/index.module.scss';
import favicon from './favicon.ico';

/** 渲染海外站运行时外壳；页面标题和摘要由具体页面在服务端输出。 */
function Content({ appProps }: any) {
  // const languageStore = useLanguageStore((state) => state.languageStore);
  // const { userAgent } = appProps.pageProps;
  const theme = 'dark';
  // 嵌入到端上的hot_feed webview初始化不展示 title测是否来自美图APP
  // eslint-disable-next-line ban/ban

  useEffect(() => {
    // eslint-disable-next-line no-restricted-globals
    if ('scrollRestoration' in history) {
      // eslint-disable-next-line no-restricted-globals
      history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
  }, []);
  return (
    <>
      <Head>
        <link rel="icon" href={favicon.src} sizes="any" />
        <meta property="og:image" content={favicon.src} />
        <style>
          {`
        html, body {
          background: ${theme === 'light' ? '#f0f0f0ff' : '#0a0a0aff'} !important;
        }
     `}
        </style>
      </Head>

      {/* Facebook Pixel */}
      <Script
        id="facebook-pixel"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '874991374996562');
              fbq('track', 'PageView');
            `,
        }}
      />
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: 'none' }}
          src="https://www.facebook.com/tr?id=874991374996562&ev=PageView&noscript=1"
          alt=""
        />
      </noscript>

      {/* Google Analytics */}
      <Script
        id="google-analytics-script"
        src="https://www.googletagmanager.com/gtag/js?id=G-GJ1BR2FCKG"
        strategy="afterInteractive"
      />
      <Script
        id="google-analytics-init"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
          window.dataLayer = window.dataLayer || [];
          function gtag(){window.dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'G-GJ1BR2FCKG');
        `,
        }}
      />

      {/* 导航组件 */}
      <NextPagesNavigator navigate={navigate} />

      {/* 主内容区 */}
      <div className={styles.layoutContainer}>
        {renderNextPage(appProps)}
      </div>
    </>
  );
}

export default defineNextSubApp<{
  InitialData: {};
}>({
  component: ({ appProps, initialData }) => {
    return (
      <Content appProps={appProps} initialData={initialData} />
    );
  },
  // 在浏览器内、水合开始之前、也就是用户可以与页面交互之前，完成埋点初始化等操作。
  async beforeHydration(data: any) {
    let stage = 'channelAndLanguage';
    try {
      initChannel();
      const userLang = localStorageUtil.getItem('lang');
      const searchParams = new URLSearchParams(window.location.search);
      const roomId = searchParams.get('room_id') || '';
      let lang: LanguageCodeType = getIsOverseaByCountryCode(data.props.pageProps.countryCode) ? LanguageCode.en : LanguageCode.zhHans;
      const urlLanguageCode = searchParams.get('language_code');
      const acceptLang = navigator.language.toLowerCase();
      // 链接参数
      if (isInLangList(urlLanguageCode as LanguageCodeType)) {
        lang = mapLanguageCode(urlLanguageCode as LanguageCodeType);
        // local缓存参数
      } else if (isInLangList(userLang as LanguageCodeType)) {
        lang = mapLanguageCode(userLang as LanguageCodeType);
        // 浏览参数
      } else if (acceptLang) {
        console.log(acceptLang, 'acceptLang');
        lang = mapLanguageCode(acceptLang as LanguageCodeType);
      }
      const channelValue = getChannel();
      const utm = extractUTMParams();
      stage = 'mtstat';
      await loadMtstatSdkScript();
      await statAgent.initialize({
        appKey: process.env.NEXT_PUBLIC_REBONEO_APP_KEY as string,
        uid: getUID(),
        env: getEnv().ENV === 'release' ? 'release' : 'test',
        channel: channelValue,
        moduleId: 'roboneo_web',
        commonParams: {
          utm_info: utm?.statsParams?.utm_info || '',
          room_id: roomId,
          first_url: getFirstURL(), // 渠道投放的落地页url
          channel: channelValue,
          matrix_channel: getMatrixChannel(), // 矩阵跳转时的标识
          sys_language: lang,
        },
        beforeLogin() { return true; },
        beforePageView(mtstat) {
          mtstat.setGid(getGid(), 'app');
          return true;
        },
      });
      window.mtstat?.track('roboneo_official_enter', {});
      const shareCoverUrl = getEnv().ENV === 'development'
        ? `${window.location.origin}/--oversea/share_cover`
        : `${window.location.origin}/share_cover`;

      const shareFlowUrl = getEnv().ENV === 'development'
        ? `${window.location.origin}/--oversea/flow_share`
        : `${window.location.origin}/flow_share`;

      const shareTeamUrl = getEnv().ENV === 'development'
        ? `${window.location.origin}/--roboneo/team_share`
        : `${window.location.origin}/team_share`;

      const TOKEN = {
        development: 'A2600861ECF8CBF270D24AE3AD37F4BA',
        dev: 'A2600861ECF8CBF270D24AE3AD37F4BA',
        pre: 'A2600861ECF8CBF270D24AE3AD37F4BA',
        beta: '315F4D4D9343434994287A78EAC04076',
        release: '45C30555F10E49629098A75F95828DA6',
      };

      const token = getAccessToken();
      const uid = getUID();
      stage = 'initComponent';
      InitComponent({
        clientId: process.env.NEXT_PUBLIC_REBONEO_CLIENT_ID || '',
        env: getEnv().ENV,
        gid: getGid(),
        token: TOKEN[getEnv().ENV],
        firstUrl: getFirstURL(),
        channel: channelValue,
        shareCoverUrl,
        shareFlowUrl,
        shareTeamUrl,
        currentCountryCode: data.props.pageProps.countryCode,
        languageCode: lang,
        theme: 'dark',
        google_target_id: 'G-GJ1BR2FCKG',
        apiConfig: {
          middlewareConfig: {
            errorMiddleware: createErrorMiddleware,
            logging: true,
            commonParamsHeadersMiddleware: createCommonParamsMiddleware(() => ({ extra: { big_data_patch: { position_type: window.location.pathname } } })),
          },
          canvasMiddlewareConfig: {
            errorMiddleware: createCanvasErrorMiddleware,
            logging: true,
          },
        },
        userInfo: {
          uid: (uid && token) ? uid : '',
          token: (uid && token) ? token : '',
          avatar: '',
          screenName: '',
        },
      });
      stage = 'initMTLogin';
      try {
        await initMTLogin({ isOversea: getIsOverseaByCountryCode(data.props.pageProps.countryCode) });
      } catch (error) {
        // 账号 SDK 属于非首屏依赖，初始化失败时不能阻断 SubApp resolve 并触发 Next.js 整页错误。
        Sentry.captureException(error, {
          tags: {
            app: 'oversea',
            lifecycle: 'beforeHydration',
            stage: 'initMTLogin',
          },
        });
        console.error('[Oversea] initMTLogin failed:', error);
      }

      stage = 'initNavigateMap';
      InitNavigateMap({
        historyStudio: (query) => {
          navigate({
            mode: 'url',
            url: getEnv().ENV === 'development' ? '/--roboneo/studio' : '/studio',
            query,
          });
        },
        agentCreateStudio: (query) => {
          navigate({
            mode: 'url',
            url: getEnv().ENV === 'development' ? '/--roboneo/studio' : '/studio',
            query,
          });
        },
        historyFlow: (query) => {
          navigate({
            mode: 'url',
            url: getEnv().ENV === 'development' ? '/--roboneo/ai_flow' : '/ai_flow',
            query,
          });
        },
        historyCommunity: (query) => {
          navigate({
            mode: 'url',
            url: getEnv().ENV === 'development' ? '/--roboneo/community' : '/community',
            query,
          });
        },
        historyPPT: (query) => {
          navigate({
            mode: 'url',
            openNewWindow: pageAgent.getCurrentPage()?.locationName === 'home',
            url: getEnv().ENV === 'development' ? '/--roboneo/ppt' : '/ppt',
            query,
          });
        },
        inputChatNewStudio: (query) => {
          navigate({
            mode: 'url',
            url: getEnv().ENV === 'development' ? '/--roboneo/studio' : '/studio',
            query,
          });
        },
        closePPTBackStudio: (query) => {
          navigate({
            mode: 'url',
            url: getEnv().ENV === 'development' ? '/--roboneo/studio' : '/studio',
            query,
            replace: true,
          });
        },
        agentLogoHome: (query) => {
          navigate({
            mode: 'url',
            url: getEnv().ENV === 'development' ? '/--roboneo/home' : '/home',
          });
        },
        onCheckRoomError: () => {
        // room_id 检测无效回调
        },
        historyCurrentRoomDelete: () => {
          navigate({
            mode: 'url',
            url: getEnv().ENV === 'development' ? '/--roboneo/home' : '/home',
            replace: true,
          });
        },
        history404: () => {
          navigate({
            mode: 'url',
            url: getEnv().ENV === 'development' ? '/--roboneo/404' : '/404',
          });
        },
        historyTeamFlow: (query) => {
          navigate({
            mode: 'url',
            url: getEnv().ENV === 'development' ? '/--roboneo/team_studio' : '/team_studio',
            query,
          });
        },
      });
      return {};
    } catch (error) {
      // beforeHydration 只负责增强能力，任一初始化异常都不能阻断页面进入 hydration。
      Sentry.captureException(error, {
        tags: {
          app: 'oversea',
          lifecycle: 'beforeHydration',
          stage,
        },
      });
      console.error('[Oversea] beforeHydration failed:', error);
      return {};
    }
  },
});
